import { fbTrackingPayload } from "@/lib/fbTracking";
import { ConsentCheckbox } from "@/components/ConsentCheckbox";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { sendLeadToWebhook } from "@/lib/leadWebhook";
import { Turnstile, turnstilePayload, FormStartedAt } from "@/components/Turnstile";
import { detectCountry } from "@/lib/geoCountry";
import { COUNTRIES, Country, formatDigits, lenRange, lengthMessage } from "@/lib/countries";
import { buildWhatsAppUrl, WHATSAPP_URL } from "@/components/WhatsAppButton";
import { buildTelegramUrl } from "@/components/TelegramButton";

/* ---------- shared shell ---------- */

function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="landing-light fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 py-10 sm:items-center">
      <div
        className="fixed inset-0 bg-foreground/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-lg border-2 border-foreground bg-background text-foreground"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
          <div>
            <h2 className="text-2xl font-semibold leading-tight tracking-tight">{title}</h2>
            {subtitle && (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                <span>{subtitle}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 border border-border bg-card px-3 py-1 text-xs uppercase tracking-widest transition-colors hover:bg-secondary"
          >
            Close
          </button>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

const fieldCls =
  "w-full h-11 border border-border bg-card px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent";

/* ---------- phone field (landing styling) ---------- */

export function PhoneField({
  id,
  name = "phone",
  required = true,
  defaultCode,
  onChange,
  placeholderText,
}: {
  id?: string;
  name?: string;
  required?: boolean;
  defaultCode?: string;
  onChange?: (combined: string) => void;
  placeholderText?: string;
} = {}) {
  const [country, setCountry] = useState<Country>(
    COUNTRIES.find((c) => c.code === defaultCode) || COUNTRIES[0],
  );
  const touchedCountry = useRef(false);
  const [local, setLocal] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [min, max] = lenRange(country.code);

  useEffect(() => {
    let alive = true;
    detectCountry().then((code) => {
      if (!alive || !code || touchedCountry.current) return;
      const match = COUNTRIES.find((c) => c.code === code);
      if (match) setCountry(match);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setLocal((prev) => formatDigits(prev, max));
  }, [max]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const digits = local.replace(/\D/g, "");
    if (!required && digits.length === 0) {
      el.setCustomValidity("");
      return;
    }
    el.setCustomValidity(
      digits.length >= min && digits.length <= max ? "" : lengthMessage(country.code),
    );
  }, [local, min, max, country.code]);

  const filtered = query
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(query.toLowerCase()) ||
          c.dial.includes(query) ||
          c.code.toLowerCase().includes(query.toLowerCase()),
      )
    : COUNTRIES;

  const combined = local ? `${country.dial} ${local}`.trim() : "";
  const placeholder = placeholderText || formatDigits("0123456789012345".slice(0, min), max);
  useEffect(() => {
    onChange?.(combined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combined]);

  return (
    <div ref={wrapRef} className="relative">
      <input type="hidden" name={name} value={combined} />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex h-11 shrink-0 items-center gap-2 border border-border bg-card px-3 text-sm transition-colors hover:border-accent"
          aria-label="Select country"
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="text-muted-foreground">{country.dial}</span>
          <span aria-hidden="true" className="text-xs">▾</span>
        </button>
        <input
          ref={inputRef}
          id={id}
          type="tel"
          inputMode="tel"
          required={required}
          autoComplete="tel-national"
          placeholder={placeholder}
          value={local}
          onChange={(e) => setLocal(formatDigits(e.target.value, max))}
          className={`min-w-0 flex-1 ${fieldCls}`}
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 flex max-h-64 flex-col overflow-hidden border border-border bg-background shadow-xl">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country or code…"
            className="h-10 border-b border-border bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:outline-none"
          />
          <div className="overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  touchedCountry.current = true;
                  setCountry(c);
                  setOpen(false);
                  setQuery("");
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-secondary"
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-muted-foreground">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-muted-foreground">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- free demo form modal ---------- */

export function DemoFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [state, setState] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Book your free demo"
      subtitle="Tell us where you ship — we'll show the CRM on your own lanes."
    >
      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setState("sending");
          setErrorMsg("");
          const fd = new FormData(e.currentTarget);
          const params = new URLSearchParams(window.location.search);
          const payload = {
            contact_name: String(fd.get("contact_name") || ""),
            company: String(fd.get("company") || ""),
            email: String(fd.get("email") || ""),
            phone: String(fd.get("phone") || ""),
            role: String(fd.get("role") || ""),
            corridor: String(fd.get("lane") || ""),
            needs: ["free-demo"],
            website: String(fd.get("website") || ""),
            utm_source: params.get("utm_source") || undefined,
            utm_medium: params.get("utm_medium") || undefined,
            utm_campaign: params.get("utm_campaign") || undefined,
            utm_content: params.get("utm_content") || undefined,
            utm_term: params.get("utm_term") || undefined,
            ref: "landing_demo",
            ...turnstilePayload(fd),
          ...(await fbTrackingPayload()),
          };
          void sendLeadToWebhook({
            company: payload.company,
            contactName: payload.contact_name,
            email: payload.email,
            phone: payload.phone,
            role: payload.role,
            tradeLane: payload.corridor,
            website: payload.website,
          });
          try {
            const { error } = await supabase.functions.invoke("submit-lead", { body: payload });
            if (error) throw error;
            setState("ok");
            onClose();
            window.location.href = "/thanks";
          } catch (err: any) {
            setState("error");
            setErrorMsg(err?.message || "Failed to submit. Please try again.");
            setCaptchaReset((n) => n + 1);
          }
        }}
      >
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ display: "none" }}
        />
        <input name="contact_name" required placeholder="Full name" className={fieldCls} />
        <input name="company" required placeholder="Company name" className={fieldCls} />
        <input name="email" type="email" required placeholder="Work email" className={fieldCls} />
        <PhoneField />
        <select name="role" required defaultValue="" className={`${fieldCls} px-3`}>
          <option value="" disabled>
            Role
          </option>
          <option value="forwarder">Forwarder</option>
          <option value="carrier-rail">Carrier Rail</option>
          <option value="carrier-ocean">Carrier Ocean</option>
          <option value="shipping-line">Shipping Line</option>
          <option value="agent-nvocc">Agent / NVOCC</option>
          <option value="other">Other</option>
        </select>
        <input
          name="lane"
          placeholder="Trade lane (e.g. Shanghai → Jebel Ali)"
          className={fieldCls}
        />
        <FormStartedAt />
        <ConsentCheckbox />
        <Turnstile theme="light" resetSignal={captchaReset} onToken={setCaptchaToken} />
        <button
          type="submit"
          disabled={state === "sending" || state === "ok" || !captchaToken}
          className="h-12 w-full border border-foreground bg-foreground text-xs uppercase tracking-widest text-background transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {state === "sending" ? "Sending…" : state === "ok" ? "Sent ✓" : "Book free demo"}
        </button>
        {state === "error" && (
          <p className="pt-1 text-center text-xs text-destructive">{errorMsg}</p>
        )}
      </form>
    </Modal>
  );
}

/* ---------- messenger modal ---------- */

export function MessengerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [wa, setWa] = useState(WHATSAPP_URL);
  const [tg, setTg] = useState("https://t.me/findrates_bot");

  useEffect(() => {
    if (!open) return;
    setWa(buildWhatsAppUrl());
    setTg(buildTelegramUrl());
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send one request."
      subtitle="Pick a channel — we reply with two verified quotes in 6 hours."
    >
      <div className="grid gap-0 border border-border sm:grid-cols-2">
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-card px-6 py-6 text-center text-xs uppercase tracking-widest transition-colors hover:bg-secondary"
        >
          WhatsApp
        </a>
        <a
          href={tg}
          target="_blank"
          rel="noopener noreferrer"
          className="border-t border-border bg-foreground px-6 py-6 text-center text-xs uppercase tracking-widest text-background transition-opacity hover:opacity-90 sm:border-l sm:border-t-0"
        >
          Telegram
        </a>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
        <span>Prefer email? </span>
        <a href="mailto:request@findrates.ai" className="text-accent-strong underline">
          request@findrates.ai
        </a>
      </p>
    </Modal>
  );
}
